import { useEffect, useState } from "react";

export interface UtmParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
  fbclid: string | null;
  msclkid: string | null;
  landing_page: string | null;
  referrer: string | null;
}

const STORAGE_KEY = "utm_params";

export const useUtmParams = (): UtmParams => {
  const [params, setParams] = useState<UtmParams>(() => {
    // Try to get stored params first
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        // If parsing fails, continue to capture from URL
      }
    }
    return {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_term: null,
      utm_content: null,
      gclid: null,
      fbclid: null,
      msclkid: null,
      landing_page: null,
      referrer: null,
    };
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    const newParams: UtmParams = {
      utm_source: urlParams.get("utm_source"),
      utm_medium: urlParams.get("utm_medium"),
      utm_campaign: urlParams.get("utm_campaign"),
      utm_term: urlParams.get("utm_term"),
      utm_content: urlParams.get("utm_content"),
      gclid: urlParams.get("gclid"),
      fbclid: urlParams.get("fbclid"),
      msclkid: urlParams.get("msclkid"),
      landing_page: window.location.pathname + window.location.search,
      referrer: document.referrer || null,
    };

    // Only update if we have new UTM data (first visit with params)
    const hasNewParams = Object.values(newParams).some(
      (v, i) => v !== null && i < 8 // Check only UTM and click ID params
    );

    if (hasNewParams) {
      setParams(newParams);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newParams));
    } else {
      // If no new params but we have stored ones, keep them
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const storedParams = JSON.parse(stored);
          // Update landing page and referrer if not set
          if (!storedParams.landing_page) {
            storedParams.landing_page = newParams.landing_page;
          }
          if (!storedParams.referrer) {
            storedParams.referrer = newParams.referrer;
          }
          setParams(storedParams);
        } catch {
          // Keep current params
        }
      }
    }
  }, []);

  return params;
};

export default useUtmParams;
